import { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, MessageSquareText, Search, ChevronDown, ChevronUp, RefreshCw, Smartphone } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { analyzeSms } from '@/lib/smsSegments';
import { sendAdhocSms, listSmsLog, type SmsLogRow } from '@/lib/smsApi';
import { supabase } from '@/lib/supabaseClient';

// Which app sent it — recorded on every row (see sms_log.source) so the
// ERP's unfiltered, everyone's-history view can show origin. rxk's own
// history below is additionally scoped to the signed-in staff member's
// own sends only (see listSmsLog's sentBy param) — same table/RLS as ERP,
// just a narrower app-level view here.
const SOURCE = 'rxk_admin';

// No AppShell here on purpose — this renders as one tab inside the
// Communications hub (see admin/communications/page.tsx), which owns the
// single AppShell for both this and PhoneContent.
export function SmsContent() {
  const { push } = useToast();
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [rows, setRows] = useState<SmsLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not signed in');
      setRows(await listSmsLog(user.id));
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => {
    load();
  }, [load]);

  const analysis = useMemo(() => analyzeSms(message), [message]);
  const chipTone = analysis.segments > 1 ? 'error' : analysis.encoding === 'Unicode' ? 'warning' : 'primary';
  const canSend = phone.trim().length >= 7 && message.trim().length > 0 && !sending;

  const handleSend = async () => {
    const to = phone.trim();
    const msg = message.trim();
    if (!to) {
      push('error', 'Enter a phone number');
      return;
    }
    if (!msg) {
      push('error', 'Enter a message');
      return;
    }
    setSending(true);
    try {
      const result = await sendAdhocSms(to, msg, SOURCE);
      if (result.ok) {
        push('success', `SMS sent to ${to}`);
        setPhone('');
        setMessage('');
      } else {
        push('error', result.error || 'SMS failed to send');
      }
      load(); // a failed send still writes a row — refresh either way
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.to_phone.toLowerCase().includes(q) ||
        (r.sent_by_name ?? '').toLowerCase().includes(q) ||
        r.message.toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
      <div className="space-y-5">
        <div>
          <h1 className="text-[18px] font-bold text-ink">Send SMS</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">
            Send a one-off text to any phone number — the history below is shared with the ERP, so every message sent from either app shows up here.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-5">
            <Card>
              <CardHeader title="New Message" icon={<Send className="w-4 h-4" />} />
              <CardBody className="space-y-4">
                <Field label="Phone Number">
                  <div className="relative">
                    <Smartphone className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                    <Input
                      placeholder="e.g. 01712345678"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      inputMode="tel"
                      className="pl-8"
                    />
                  </div>
                </Field>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-[11px] font-semibold text-ink-faint tracking-wide">Message</label>
                    <Badge tone={chipTone}>
                      {analysis.length}/{analysis.segmentLimit} · {analysis.segments} SMS
                      {analysis.encoding === 'Unicode' ? ' (Unicode)' : ''}
                    </Badge>
                  </div>
                  <Textarea
                    rows={6}
                    placeholder="Type the message to send..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  {analysis.encoding === 'Unicode' && (
                    <p className="text-[11px] text-warning mt-1.5">
                      Contains non-GSM characters (e.g. Bangla script or emoji) — billed as Unicode SMS, 70 chars per
                      segment instead of 160.
                    </p>
                  )}
                  {analysis.segments > 1 && (
                    <p className="text-[11px] text-error mt-1.5">
                      This will be billed as {analysis.segments} SMS segments, not 1.
                    </p>
                  )}
                </div>

                <Button type="button" className="w-full" onClick={handleSend} disabled={!canSend} loading={sending}>
                  <Send className="w-3.5 h-3.5" /> Send SMS
                </Button>
              </CardBody>
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card>
              <CardHeader
                title="Your History"
                subtitle={loading ? undefined : `${rows.length} message${rows.length === 1 ? '' : 's'} sent by you — full history across all staff is in the ERP`}
                icon={<MessageSquareText className="w-4 h-4" />}
                action={
                  <button
                    type="button"
                    onClick={load}
                    title="Refresh"
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                }
              />
              <CardBody className="space-y-3">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                  <Input
                    placeholder="Search by phone, message, or staff"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>

                {loading ? (
                  <Spinner />
                ) : filtered.length === 0 ? (
                  <EmptyState
                    icon={<MessageSquareText className="w-5 h-5" />}
                    title={rows.length === 0 ? 'No messages sent yet' : 'No matches'}
                    hint={rows.length === 0 ? 'Sent SMS will show up here.' : 'Try a different phone number, name, or keyword.'}
                  />
                ) : (
                  <div className="space-y-2">
                    {filtered.map((r) => {
                      const expanded = expandedId === r.id;
                      return (
                        <div key={r.id} className="rounded-xl border border-border-subtle overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? null : r.id)}
                            className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-surface-muted cursor-pointer text-left"
                          >
                            <Badge tone={r.status === 'sent' ? 'success' : 'error'}>
                              {r.status === 'sent' ? 'Sent' : 'Failed'}
                            </Badge>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold text-ink truncate">{r.to_phone}</p>
                              <p className="text-[12px] text-ink-muted truncate">{r.message}</p>
                              <p className="text-[11px] text-ink-faint mt-0.5">
                                {r.sent_by_name ?? 'Unknown Staff'} · {new Date(r.created_at).toLocaleString()}
                              </p>
                            </div>
                            {expanded ? (
                              <ChevronUp className="w-3.5 h-3.5 text-ink-faint flex-shrink-0 mt-0.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-ink-faint flex-shrink-0 mt-0.5" />
                            )}
                          </button>
                          {expanded && (
                            <div className="px-3 pb-3 border-t border-border-subtle pt-2.5 space-y-1.5">
                              <p className="text-[12.5px] text-ink whitespace-pre-wrap">{r.message}</p>
                              {r.status === 'failed' && r.error && (
                                <p className="text-[11.5px] text-error">Error: {r.error}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
  );
}
